import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';

const formatErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Helper exported for admin components to attach the Bearer token.
export const getAuthToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (err) {
    console.warn('[auth] localStorage read failed:', err);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, check if there's an active session via the saved Bearer token.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getAuthToken();
      if (!token) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          if (response.ok) {
            const data = await response.json();
            setUser(data);
          } else {
            // Token invalid/expired — clear it
            try {
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(USER_KEY);
            } catch (err) {
              console.warn('[auth] localStorage cleanup failed:', err);
            }
            setUser(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[auth] session check failed:', err);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistSession = (data) => {
    try {
      if (data?.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
      if (data?.user) localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    } catch (err) {
      console.warn('[auth] failed to persist session:', err);
    }
  };

  const login = useCallback(async (email, password) => {
    const response = await fetch(`${API_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(formatErrorDetail(data?.detail) || 'Login failed');
    }
    persistSession(data);
    // Immediately re-fetch /me so we have permissions + must_change_password
    try {
      const meRes = await fetch(`${API_URL}/api/admin/me`, {
        headers: { Authorization: `Bearer ${data.access_token}` },
      });
      if (meRes.ok) {
        const me = await meRes.json();
        setUser(me);
        return { ...data, user: me };
      }
    } catch (err) {
      console.warn('[auth] /me fetch after login failed:', err);
    }
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const response = await fetch(`${API_URL}/api/admin/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(formatErrorDetail(data?.detail) || 'Registration failed');
    }
    persistSession(data);
    try {
      const meRes = await fetch(`${API_URL}/api/admin/me`, {
        headers: { Authorization: `Bearer ${data.access_token}` },
      });
      if (meRes.ok) {
        const me = await meRes.json();
        setUser(me);
        return { ...data, user: me };
      }
    } catch (err) {
      console.warn('[auth] /me fetch after register failed:', err);
    }
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    const token = getAuthToken();
    try {
      if (token) {
        await fetch(`${API_URL}/api/admin/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (err) {
      console.warn('[auth] logout request failed, proceeding with local cleanup:', err);
    }
    setUser(null);
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch (err) {
      console.warn('[auth] localStorage cleanup failed:', err);
    }
  }, []);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    hasPermission: (perm) => !!user?.permissions?.includes(perm),
    permissions: user?.permissions || [],
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
